import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, ListGroup, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCommentSlash
} from '@fortawesome/free-solid-svg-icons';

const AnalysisTab = ({ meetingsData, formatParticipantName }) => {
  const unmetCompanyWishes = meetingsData.unmet_company_wishes || [];
  const unmetPartnerWishes = meetingsData.unmet_partner_wishes || [];
  const participantConflicts = meetingsData.participant_conflicts || [];

  const totalIssuesAndUnmetWishes =
    participantConflicts.length +
    unmetCompanyWishes.length +
    unmetPartnerWishes.length;

  if (totalIssuesAndUnmetWishes === 0) {
    return (
      <Alert variant="success" className="mt-3">
        Aucun problème de planification détecté et tous les vœux semblent
        satisfaits.
      </Alert>
    );
  }

  return (
    <Accordion
      defaultActiveKey={
        participantConflicts.length > 0
          ? '0'
          : unmetCompanyWishes.length > 0 || unmetPartnerWishes.length > 0
          ? '1'
          : null
      }
      flush
      className="mt-3"
    >
      {participantConflicts.length > 0 && (
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-danger me-2"
            />
            Conflits ({participantConflicts.length})
          </Accordion.Header>
          <Accordion.Body className="p-0">
            <ListGroup variant="flush">
              {participantConflicts.map(conflict => (
                <ListGroup.Item
                  key={`conflict-${conflict.id}-${conflict.participantType}-${conflict.participantId}`}
                >
                  <p className="mb-1 fs--1 text-danger">
                    {conflict.message} pour{' '}
                    {formatParticipantName(conflict.participant)} (
                    {conflict.participantType} ID: {conflict.participantId})
                  </p>
                  <ul className="fs--2 ps-3">
                    {conflict.conflictingMeetings?.map(
                      (
                        m // Vérifier l'existence de conflictingMeetings
                      ) => (
                        <li key={`cm-${m.id}`}>
                          RDV ID {m.id}: avec{' '}
                          {conflict.participantType === 'Entreprise'
                            ? formatParticipantName(
                                m.partner_participants?.[0]
                              ) ||
                              m.withPartner ||
                              'Partenaire N/A'
                            : formatParticipantName(
                                m.company_participants?.[0]
                              ) ||
                              m.withCompany ||
                              'Entreprise N/A'}
                        </li>
                      )
                    )}
                  </ul>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Accordion.Body>
        </Accordion.Item>
      )}

      {(unmetCompanyWishes.length > 0 || unmetPartnerWishes.length > 0) && (
        <Accordion.Item eventKey="1">
          <Accordion.Header>
            <FontAwesomeIcon
              icon={faCommentSlash}
              className="text-warning me-2"
            />
            Vœux Non Satisfaits (
            {unmetCompanyWishes.length + unmetPartnerWishes.length})
          </Accordion.Header>
          <Accordion.Body className="p-0">
            {unmetCompanyWishes.length > 0 && (
              <>
                <h6 className="px-3 pt-3 pb-1 fs-0 text-body-emphasis">
                  Vœux d'entreprises non satisfaits :
                </h6>
                <ListGroup variant="flush" className="mb-3">
                  {unmetCompanyWishes.map(wish => (
                    <ListGroup.Item key={`ucw-${wish.id}`}>
                      <p className="mb-1 fs--1">
                        Les participants (<strong>{wish.participants}</strong>)
                        de <strong>{wish.company_name}</strong> (ID:{' '}
                        {wish.company_id}) souhaitaient rencontrer{' '}
                        <strong>{wish.partner_name}</strong> (Partenaire ID:{' '}
                        {wish.partner_id}).
                      </p>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
            {unmetPartnerWishes.length > 0 && (
              <>
                <h6 className="px-3 pt-3 pb-1 fs-0 text-body-emphasis">
                  Vœux de partenaires non satisfaits :
                </h6>
                <ListGroup variant="flush">
                  {unmetPartnerWishes.map(wish => (
                    <ListGroup.Item key={`upw-${wish.id}`}>
                      <p className="mb-1 fs--1">
                        Le participant <strong>{wish.participant}</strong> de{' '}
                        <strong>{wish.partner_name}</strong> (ID:{' '}
                        {wish.partner_id}) souhaitait rencontrer{' '}
                        <strong>{wish.company_name}</strong> (Entreprise ID:{' '}
                        {wish.company_id}).
                      </p>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
          </Accordion.Body>
        </Accordion.Item>
      )}
    </Accordion>
  );
};

AnalysisTab.propTypes = {
  meetingsData: PropTypes.shape({
    unmet_company_wishes: PropTypes.array,
    unmet_partner_wishes: PropTypes.array,
    participant_conflicts: PropTypes.array
    // N'a pas besoin de meetings, company_participants, partner_participants directement
    // si les données de conflits et vœux sont déjà bien structurées.
  }).isRequired,
  formatParticipantName: PropTypes.func.isRequired
};

export default AnalysisTab;
